import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PhoneNumberData {
  phoneNumber: { phoneNumber: string; purchasedAt: string; monthlyCostCents: number } | null;
}

export function PhoneNumberCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const areaCodeInputId = useId();
  const [areaCode, setAreaCode] = useState("");
  const [confirmingRelease, setConfirmingRelease] = useState(false);

  const { data, isLoading } = useQuery<PhoneNumberData>({
    queryKey: ["/api/phone-number"],
  });

  const purchaseMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/phone-number/purchase", {
        ...(areaCode.trim() ? { areaCode: Number(areaCode.trim()) } : {}),
      }),
    onSuccess: () => {
      toast({ title: "Phone number purchased", description: "Your AI assistant can now place outbound calls." });
      queryClient.invalidateQueries({ queryKey: ["/api/phone-number"] });
    },
    onError: (err: any) => toast({ title: "Purchase failed", description: err.message, variant: "destructive" }),
  });

  const releaseMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", "/api/phone-number"),
    onSuccess: () => {
      toast({ title: "Phone number released", description: "The monthly fee has stopped. You can purchase a new number anytime." });
      queryClient.invalidateQueries({ queryKey: ["/api/phone-number"] });
      setConfirmingRelease(false);
    },
    onError: (err: any) => {
      toast({ title: "Release failed", description: err.message, variant: "destructive" });
      setConfirmingRelease(false);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const number = data?.phoneNumber ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Outbound Phone Number</CardTitle>
        <CardDescription>
          A dedicated phone number your AI assistant uses to place real calls on your behalf (booking
          appointments, making reservations, and more). The monthly fee and per-minute call costs are billed to
          your account like other AI usage.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {number ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between border rounded-lg p-4">
              <div>
                <div className="text-lg font-semibold" data-testid="text-phone-number">{number.phoneNumber}</div>
                <div className="text-sm text-muted-foreground">
                  Active since {format(new Date(number.purchasedAt), "MMMM d, yyyy")}
                </div>
              </div>
              {confirmingRelease ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => releaseMutation.mutate()}
                    disabled={releaseMutation.isPending}
                    data-testid="button-confirm-release-number"
                  >
                    {releaseMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Yes, release it
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmingRelease(false)} data-testid="button-cancel-release-number">
                    Keep number
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setConfirmingRelease(true)} data-testid="button-release-number">
                  Release number
                </Button>
              )}
            </div>
            {confirmingRelease && (
              <p className="text-sm text-muted-foreground">
                Releasing the number stops the monthly fee immediately. The number goes back to the carrier and
                may not be recoverable. Your assistant won't be able to place calls until you purchase a new one.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You don't have a phone number yet. Purchase one to enable real outbound calling. If you cancel your
              subscription, the number is released automatically.
            </p>
            <div className="flex items-end gap-3">
              <div>
                <label className="text-sm font-medium block mb-1.5" htmlFor={areaCodeInputId}>
                  Preferred area code (optional)
                </label>
                <Input
                  id={areaCodeInputId}
                  data-testid="input-area-code"
                  placeholder="e.g. 310"
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                  className="w-40"
                />
              </div>
              <Button
                onClick={() => purchaseMutation.mutate()}
                disabled={purchaseMutation.isPending}
                data-testid="button-purchase-number"
              >
                {purchaseMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Purchase number
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
